// SPDX-License-Identifier: AGPL-3.0-only
// Copyright (C) 2025 realvirtual GmbH <https://realvirtual.io>

/**
 * SetTransformDialog — Draggable floating panel for setting position and
 * rotation of layout objects.
 *
 * Reads the selected node the SAME way as PropertyInspector: via
 * useEditorPlugin().state.selectedNodePath + viewer.registry.getNode().
 * This guarantees both panels always show the same values.
 */

import { useState, useCallback, useEffect, useRef, useSyncExternalStore } from 'react';
import {
  Paper,
  Button,
  Box,
  Typography,
  IconButton,
  Tooltip,
} from '@mui/material';
import { Close, RestartAlt } from '@mui/icons-material';
import { MathUtils } from 'three';
import type { RVViewer } from '../rv-viewer';
import { USER_PAUSE_REASON } from '../engine/rv-constants';
import { Vector3Editor } from './rv-field-editors';
import { useViewer } from '../../hooks/use-viewer';
import { useEditorPlugin } from '../../hooks/use-editor-plugin';

// ─── Module-level open/close store (minimal — just tracks open state) ───

let _open = false;
const _listeners = new Set<() => void>();
function notify() { for (const l of _listeners) l(); }

/** Open the Set Transform dialog. Syncs the extras editor selection so the dialog reads the correct node. */
export function openSetPositionDialog(viewer: RVViewer, paths: string[]): void {
  // Ensure the extras editor's selectedNodePath matches the target path.
  // PropertyInspector reads from this same source, so this keeps them in sync.
  if (paths.length > 0) {
    const editorPlugin = viewer.getPlugin?.('rv-extras-editor') as
      { selectNode?: (path: string, showInspector: boolean) => void } | undefined;
    if (editorPlugin?.selectNode) {
      editorPlugin.selectNode(paths[0], false);
    }
  }
  _open = true;
  notify();
}

/** Close the Set Transform dialog. */
export function closeSetPositionDialog(): void {
  if (!_open) return;
  _open = false;
  notify();
}

function subscribe(listener: () => void) {
  _listeners.add(listener);
  return () => { _listeners.delete(listener); };
}
function getSnapshot() { return _open; }

// ─── Drag hook ──────────────────────────────────────────────────────────

function useDrag(initialPos: { x: number; y: number }) {
  const [pos, setPos] = useState(initialPos);
  const dragging = useRef(false);
  const offset = useRef({ x: 0, y: 0 });

  const onPointerDown = useCallback((e: React.PointerEvent) => {
    const tag = (e.target as HTMLElement).tagName;
    if (tag === 'BUTTON' || tag === 'INPUT' || tag === 'SVG' || tag === 'PATH') return;
    dragging.current = true;
    offset.current = { x: e.clientX - pos.x, y: e.clientY - pos.y };
    e.currentTarget.setPointerCapture(e.pointerId);
  }, [pos]);

  const onPointerMove = useCallback((e: React.PointerEvent) => {
    if (!dragging.current) return;
    setPos({ x: e.clientX - offset.current.x, y: e.clientY - offset.current.y });
  }, []);

  const onPointerUp = useCallback(() => {
    dragging.current = false;
  }, []);

  return { pos, setPos, handlers: { onPointerDown, onPointerMove, onPointerUp } };
}

// ─── Component ──────────────────────────────────────────────────────────

export function SetPositionDialog() {
  const open = useSyncExternalStore(subscribe, getSnapshot);
  const viewer = useViewer();
  const { state } = useEditorPlugin();
  const selectedPath = state.selectedNodePath;

  const [position, setPosition] = useState({ x: 0, y: 0, z: 0 });
  const [rotation, setRotation] = useState({ x: 0, y: 0, z: 0 });
  const drag = useDrag({ x: Math.round(window.innerWidth / 2 - 160), y: Math.round(window.innerHeight / 2 - 120) });
  const editingRef = useRef(false);

  // Get the node — same way as PropertyInspector, with fallback to SelectionManager
  let node = selectedPath ? viewer.registry?.getNode(selectedPath) ?? null : null;
  if (!node) {
    // Fallback: try the SelectionManager's primary path
    const selPath = viewer.selectionManager?.getSnapshot().primaryPath;
    if (selPath) node = viewer.registry?.getNode(selPath) ?? null;
  }

  // Read transform from node into local state
  const readNodeTransform = useCallback(() => {
    if (!node) return;
    setPosition({
      x: +node.position.x.toFixed(4),
      y: +node.position.y.toFixed(4),
      z: +node.position.z.toFixed(4),
    });
    setRotation({
      x: +MathUtils.radToDeg(node.rotation.x).toFixed(2),
      y: +MathUtils.radToDeg(node.rotation.y).toFixed(2),
      z: +MathUtils.radToDeg(node.rotation.z).toFixed(2),
    });
  }, [node]);

  // Seed on open
  useEffect(() => {
    if (!open) return;
    readNodeTransform();
    drag.setPos({ x: Math.round(window.innerWidth / 2 - 160), y: Math.round(window.innerHeight / 2 - 120) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, selectedPath]);

  // Poll at 200ms for live gizmo updates
  useEffect(() => {
    if (!open) return;
    const id = setInterval(() => {
      if (!editingRef.current) readNodeTransform();
    }, 200);
    return () => clearInterval(id);
  }, [open, readNodeTransform]);

  // Apply user edits
  const applyTransform = useCallback((pos: { x: number; y: number; z: number }, rot: { x: number; y: number; z: number }) => {
    if (!node) return;
    const rv = node.userData?.realvirtual as Record<string, Record<string, unknown>> | undefined;
    if (rv?.LayoutObject?.Locked) return;

    node.position.set(pos.x, pos.y, pos.z);
    node.rotation.set(MathUtils.degToRad(rot.x), MathUtils.degToRad(rot.y), MathUtils.degToRad(rot.z));
    node.updateMatrixWorld(true);

    // Auto-stop the simulation when the user changes a placed object's
    // transform via the dialog. Uses the user-owned pause reason so the
    // toolbar Play button (or Space) resumes it.
    viewer.setSimulationPaused?.(USER_PAUSE_REASON, true);

    const path = viewer.registry?.getPathForNode(node) ?? selectedPath ?? '';
    viewer.emit('layout-transform-update', {
      path,
      position: [pos.x, pos.y, pos.z] as [number, number, number],
      rotation: [rot.x, rot.y, rot.z] as [number, number, number],
    });
    viewer.markRenderDirty();
  }, [node, viewer, selectedPath]);

  const handlePositionChange = useCallback((v: { x: number; y: number; z: number }) => {
    editingRef.current = true;
    setPosition(v);
    applyTransform(v, rotation);
    setTimeout(() => { editingRef.current = false; }, 300);
  }, [applyTransform, rotation]);

  const handleRotationChange = useCallback((v: { x: number; y: number; z: number }) => {
    editingRef.current = true;
    setRotation(v);
    applyTransform(position, v);
    setTimeout(() => { editingRef.current = false; }, 300);
  }, [applyTransform, position]);

  // ESC to close
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') closeSetPositionDialog(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open]);

  if (!open || !node) return null;

  const labelSx = { fontSize: 10, color: 'text.secondary', width: 55, flexShrink: 0 };

  return (
    <Paper
      elevation={8}
      sx={{
        position: 'fixed',
        left: drag.pos.x,
        top: drag.pos.y,
        zIndex: 2000,
        width: 320,
        bgcolor: 'rgba(30, 30, 30, 0.95)',
        backdropFilter: 'blur(calc(12px * var(--rv-ui-blur-scale, 1)))',
        border: '1px solid rgba(255,255,255,0.1)',
        borderRadius: 1,
        pointerEvents: 'auto',
        overflow: 'hidden',
      }}
    >
      {/* Title bar — draggable */}
      <Box
        {...drag.handlers}
        sx={{
          display: 'flex',
          alignItems: 'center',
          px: 1.5,
          py: 0.75,
          bgcolor: 'rgba(255,255,255,0.04)',
          borderBottom: '1px solid rgba(255,255,255,0.06)',
          cursor: 'move',
          userSelect: 'none',
        }}
      >
        <Typography sx={{ fontSize: 11, fontWeight: 600, flex: 1, color: 'text.primary' }}>
          Set Transform
        </Typography>
        <IconButton
          size="small"
          onClick={closeSetPositionDialog}
          onPointerDown={(e) => e.stopPropagation()}
          sx={{ p: 0.25, color: 'text.secondary' }}
        >
          <Close sx={{ fontSize: 14 }} />
        </IconButton>
      </Box>

      {/* Position */}
      <Box sx={{ px: 1.5, pt: 1, pb: 0.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
          <Typography sx={labelSx}>Position</Typography>
          <Box sx={{ flex: 1 }}>
            <Vector3Editor value={position} onChange={handlePositionChange} />
          </Box>
          <Tooltip title="Reset position to 0">
            <IconButton size="small" onClick={() => handlePositionChange({ x: 0, y: 0, z: 0 })} sx={{ p: 0.25, ml: 0.5, color: 'text.secondary' }}>
              <RestartAlt sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography sx={labelSx}>Rotation</Typography>
          <Box sx={{ flex: 1 }}>
            <Vector3Editor value={rotation} onChange={handleRotationChange} />
          </Box>
          <Tooltip title="Reset rotation to 0">
            <IconButton size="small" onClick={() => handleRotationChange({ x: 0, y: 0, z: 0 })} sx={{ p: 0.25, ml: 0.5, color: 'text.secondary' }}>
              <RestartAlt sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Footer */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 1.5, py: 0.75, borderTop: '1px solid rgba(255,255,255,0.06)' }}>
        <Button size="small" onClick={closeSetPositionDialog} sx={{ fontSize: 10, textTransform: 'none', color: 'text.secondary', minWidth: 0, px: 1 }}>
          Close
        </Button>
      </Box>
    </Paper>
  );
}
